import moment from 'moment';
import { ScheduleHistory } from '../../../hooks/useScheduleReporting';
import { TimezoneDisplay } from './history-constants';

/**
 * Calculates the day difference between a date and a reference schedule day
 * @param dateToCompare - The date to compare against the schedule day
 * @param scheduleDay - The reference schedule day
 * @returns The number of days difference (positive if dateToCompare is after scheduleDay)
 */
export const calculateDaysDifference = (dateToCompare: moment.Moment, scheduleDay: string): number => {
  const scheduleDayMoment = moment(scheduleDay).startOf('day');
  const dateToCompareDayStart = dateToCompare.clone().startOf('day');
  return dateToCompareDayStart.diff(scheduleDayMoment, 'days');
};

/**
 * Formats a time with timezone abbreviation and optional day indicator
 * @param time - The moment object representing the time
 * @param dayIndicator - The day indicator string to append (if any)
 * @returns Formatted time string with timezone abbreviation and day indicator
 */
export const formatTimeWithTz = (time: moment.Moment, dayIndicator: string = ''): string => {
  return time.format('h:mm A') + ' ' + time.format('z') + dayIndicator;
};

/**
 * Creates a day indicator string (e.g., " +1", " +2") based on the days difference
 * @param daysDiff - Number of days difference
 * @returns The day indicator string or empty string if no difference
 */
export const getDayIndicator = (daysDiff: number): string => {
  return daysDiff > 0 ? ` +${daysDiff}` : '';
};

/**
 * Converts seconds to hours
 * @param seconds - The number of seconds to convert
 * @returns The number of hours as a string
 */
export const secondsToHours = (seconds: number) => {
  if (!seconds) return 0;
  const hours = seconds / 3600;
  return Number.isInteger(hours) ? hours.toString() : hours.toFixed(1);
};

/**
 * Formats scheduled time strings for display with appropriate timezone
 *
 * @param timeString - Time in "HH:MM:SS" format
 * @param timezone - The timezone identifier (e.g., "US/Pacific")
 * @param scheduleDay - The reference day for the schedule
 * @returns Formatted time with timezone abbreviation and day indicator if applicable
 *
 * We don't display a day indactor for listener timezone because scheduling doesn't extend past midnight in listener timezone.
 * We check to see if the time in central has crossed over into the next day and if so we display the +X indicator.
 */
export const formatScheduledStart = (
  timeString: string | null,
  timezone: string | null,
  scheduleDay: string,
  timezoneDisplay: string,
) => {
  if (!timeString) return '-';

  const fullDateTime = `${scheduleDay} ${timeString}`;

  if (timezoneDisplay === TimezoneDisplay.Listener && timezone) {
    const timeInListenerTz = moment.tz(fullDateTime, timezone);
    return formatTimeWithTz(timeInListenerTz);
  }

  const timeInOriginalTz = timezone ? moment.tz(fullDateTime, timezone) : moment.utc(fullDateTime);
  const timeInCentralTz = timeInOriginalTz.tz('America/Chicago');
  const daysDiff = calculateDaysDifference(timeInCentralTz, scheduleDay);
  const dayIndicator = getDayIndicator(daysDiff);

  return formatTimeWithTz(timeInCentralTz, dayIndicator);
};

/**
 * Formats date-time strings (actual start/end times) based on timezone preferences
 *
 * @param dateTimeString - Full ISO date-time string in UTC format (YYYY-MM-DDTHH:MM:SS.sssZ)
 * @param listenerTz - The listener's timezone identifier (e.g., "US/Pacific")
 * @param scheduleDay - The reference day for the schedule
 * @returns Formatted time with timezone abbreviation and day indicator if applicable
 *
 * Unlike scheduled times, actual times can be any date/time and are stored in UTC format,
 * so we show the day indicator in both timezone display modes.
 */
export const formatDateTime = (
  dateTimeString: string | null,
  listenerTz: string | null,
  scheduleDay: string,
  timezoneDisplay: string,
) => {
  if (!dateTimeString) return '-';

  const utcMoment = moment.utc(dateTimeString);
  const daysDiff = calculateDaysDifference(moment(dateTimeString), scheduleDay);
  const dayIndicator = getDayIndicator(daysDiff);

  const timezone = timezoneDisplay === TimezoneDisplay.Listener && listenerTz ? listenerTz : 'America/Chicago';
  const timeInTz = utcMoment.tz(timezone);

  return formatTimeWithTz(timeInTz, dayIndicator);
};

/**
 * Calculates the number of hours active during a listener's schedule.
 *
 * @param history - The schedule history object
 * @returns The number of hours active during the schedule
 *
 * We calculate the number of hours active during the schedule by computing the "effective" start and end times.
 * The effective start time is the later of the actual start time and the scheduled start time.
 * The effective end time is the earlier of the actual end time and the scheduled end time.
 * We then compute the difference between the effective start and end times in hours.
 * If the difference is negative, we add 24 hours to the difference to account for the next day.
 */
export const calculateHoursActiveDuringSchedule = (history: ScheduleHistory) => {
  if (!history.scheduled_start || !history.scheduled_end) return 0;
  if (!history.started_at || !history.ended_at) return 0;

  const scheduleDay = history.schedule_day || moment().format('YYYY-MM-DD');
  const timezone = history.timezone || 'America/Chicago';
  const scheduledStart = moment.tz(`${scheduleDay} ${history.scheduled_start}`, timezone);
  const scheduledEnd = moment.tz(`${scheduleDay} ${history.scheduled_end}`, timezone);
  const actualStart = moment.utc(history.started_at);
  const actualEnd = moment.utc(history.ended_at);
  const scheduledStartUTC = scheduledStart.clone().utc();
  const scheduledEndUTC = scheduledEnd.clone().utc();

  const effectiveStart = actualStart.isAfter(scheduledStartUTC) ? actualStart : scheduledStartUTC;
  const effectiveEnd = actualEnd.isBefore(scheduledEndUTC) ? actualEnd : scheduledEndUTC;

  const diffHours = effectiveEnd.diff(effectiveStart, 'hours');

  if (diffHours < 0) {
    const adjustedHours = diffHours + 24;
    return Number.isInteger(adjustedHours) ? adjustedHours.toString() : adjustedHours.toFixed(1);
  }

  return Number.isInteger(diffHours) ? diffHours.toString() : diffHours.toFixed(1);
};
